// Lejek koszyków dzień po dniu (TRE-2408). Kroki liczy PostHog (zdarzenia
// z ecommerce), zamówienia — baza (`product_order`). Oba źródła łączą się po
// ścieżce koszyka i wersji: `checkout_variant` × `lp_variant`.

/**
 * Wersja checkoutu Resetu z flagi `reset-checkout-ab`: `obecny`, `b`, `quiz`
 * (wejście z `?tryb=quiz`) albo `poza_testem`. Pusta tam, gdzie testu nie ma.
 */
export type CheckoutVariant = string | null;

/** Wersja landingu Resetu z flagi `reset-lp-ab`: `joanna`, `sciana` albo `stara`. */
export type LandingVariant = string | null;

export type CheckoutCartKind = "funnel" | "variant" | "product" | "path";

/**
 * Koszyk niezależnie od adresu. Ten sam wariant koszyka (`checkout_variant`)
 * działa pod `/step/`, `/v/`, `/s/`, `/przedluzenie/` i `/kupuje/`, lejek
 * (`sales_funnel`) pod `/oferta/<lejek>/checkout`, produkt pod `/c/`, `/cart/`
 * i `/embed/` — z id albo slugiem w pierwszym segmencie, a przy dwóch segmentach
 * (`/c/renewal/<id>`, `/c/sesame/<id>`) w drugim, jak w `getCheckoutStaticProps`.
 */
export const toCheckoutCartId = (path: string): string => {
  const funnel = path.match(/^\/oferta\/([^/]+)\/checkout$/);
  if (funnel) return `funnel:${funnel[1]}`;
  const variant = path.match(/^\/(?:step|v|s|przedluzenie|kupuje)\/([^/]+)/);
  if (variant) return `variant:${variant[1]}`;
  const product = path.match(/^\/(?:c|cart|embed)\/([^/]+)(?:\/([^/]+))?/);
  if (product) return `product:${product[2] ?? product[1]}`;
  return `path:${path}`;
};

export interface CheckoutFunnelCart {
  /** Wynik `toCheckoutCartId`. */
  id: string;
  kind: CheckoutCartKind;
  /** Slug lejka albo wariantu, id albo slug produktu, a dla `path` sama ścieżka. */
  slug: string;
  productName: string | null;
  /** `active`, `inactive` albo `archived`; `null` poza katalogiem. */
  status: string | null;
}

export interface CheckoutFunnelSegment {
  /** Dzień w strefie Europe/Warsaw, `YYYY-MM-DD`. */
  day: string;
  /** Ścieżka koszyka, np. `/oferta/hollywood-reset/checkout`, `/c/prod_…`, `/step/…`. */
  cart: string;
  checkoutVariant: CheckoutVariant;
  lpVariant: LandingVariant;
}

/** Każda liczba to różne osoby danego dnia, które doszły co najmniej do kroku. */
export interface CheckoutFunnelSteps {
  entries: number;
  fieldCompleted: number;
  methodSelected: number;
  payClicked: number;
  payBlocked: number;
  blikPending: number;
  blikTimeout: number;
  orderFailed: number;
  paymentFailed: number;
}

export interface CheckoutFunnelStepRow extends CheckoutFunnelSegment, CheckoutFunnelSteps {}

/** Zamówienia złożone w koszyku (bez odnowień i kolejnych rat), wg dnia założenia. */
export interface CheckoutFunnelOrderRow extends CheckoutFunnelSegment {
  paid: number;
  /** Odrzucone przez bramkę (FAILED); jedna sesja może mieć kilka prób. */
  failed: number;
  /** Porzucone albo wycofane (CANCELED). */
  canceled: number;
  /** Wciąż nieopłacone (CREATED), np. BLIK bez potwierdzenia. */
  pending: number;
  /** Wartość opłaconych pierwszych rat w zł. */
  revenue: number;
}

export type CheckoutFunnelIssueKind = "pay_blocked" | "order_failed" | "payment_failed" | "blik_pending_timeout";

export interface CheckoutFunnelIssueRow extends CheckoutFunnelSegment {
  kind: CheckoutFunnelIssueKind;
  /** Pole przy `pay_blocked`, źródło błędu przy `order_failed`, status przy `blik_pending_timeout`. */
  detail: string | null;
  /** Powód przy `pay_blocked` (`empty`/`invalid`), kod błędu bramki przy błędach płatności. */
  code: string | null;
  events: number;
  persons: number;
}

export interface CheckoutFunnelStats {
  /** Katalog lejków i wariantów koszyka (bez zarchiwizowanych) plus produkty z `/c/`. */
  carts: CheckoutFunnelCart[];
  steps: CheckoutFunnelStepRow[];
  orders: CheckoutFunnelOrderRow[];
  issues: CheckoutFunnelIssueRow[];
}
